<!--
  AboutSection - About section with image, stats badge, and feature cards

  Props:
  - backgroundColor (String): Background color of the section (default: '#E0F2FE')
  - title (String): Section title
  - titleHighlight (String): Highlighted portion of title
  - titleHighlightGradientFrom (String): Start color of title highlight gradient (default: '#11D2D4')
  - titleHighlightGradientTo (String): End color of title highlight gradient (default: '#0C7475')
  - titleColor (String): Color of title text (default: 'text-blue-dark')
  - subtitle (String): Subtitle text
  - subtitleColor (String): Color of subtitle (default: 'text-gray-700')
  - paragraphs (Array): Array of paragraph text strings
  - textColor (String): Color of text content (default: 'text-gray-700')
  - image (String): Path to main image
  - imageAlt (String): Alt text for image
  - statsLabel (String): Label for stats badge (e.g., "Über")
  - statsValue (String): Main stats value (e.g., "70%")
  - statsSubtext (String): Subtext for stats (e.g., "Trefferquote")
  - statsValueColor (String): Color of stats value (default: 'text-blue')
  - statsBadgeColor (String): Background color of stats badge (default: '#FFFFFF')
  - featureCards (Array): Array of feature card objects
  - featureCardBackgroundColor (String): Background color of feature cards (default: '#FFFFFF')
  - featureCardTitleColor (String): Color of feature card titles (default: 'text-blue')
  - featureCardTextColor (String): Color of feature card text (default: 'text-gray-600')
  - featureCardIconColor (String): Color of feature card icons (default: 'text-blue')
-->
<template>
    <section class="py-20" :class="backgroundColor">
        <div class="container mx-auto px-4 max-w-6xl">
            <div class="grid md:grid-cols-2 items-center gap-12 mb-12">
                <div class="space-y-4" :class="textColor">
                    <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" :class="titleColor">
                        <span v-html="renderTitle()"></span>
                    </h2>
                    <p v-if="subtitle" class="text-lg text-center mb-8" :class="subtitleColor">
                        {{ subtitle }}
                    </p>

                    <p v-for="(paragraph, index) in paragraphs" :key="index">
                        {{ paragraph }}
                    </p>
                </div>
                <div class="relative">
                    <div class="w-full h-fullbg-gray-300 rounded-2xl">
                        <img :alt="imageAlt" class="rounded-lg object-contain" :src="$asset(image, 'public')" />
                    </div>
                    <div
                        v-if="statsLabel && statsValue"
                        class="absolute top-[210px] lg:top-1/4 lg:-left-10 rounded-full lg:px-3 w-full lg:w-[200px] lg:h-40 flex flex-col items-center justify-center shadow-lg"
                        :style="{ backgroundColor: statsBadgeColor }"
                    >
                        <div class="text-xs text-black font-bold">{{ statsLabel }}</div>
                        <div class="text-3xl font-bold" :class="statsValueColor">{{ statsValue }}</div>
                        <div class="text-xs text-black font-bold text-center px-2">{{ statsSubtext }}</div>
                    </div>
                </div>
            </div>

            <!-- Feature Cards -->
            <div v-if="featureCards && featureCards.length" class="grid md:grid-cols-4 gap-6">
                <div v-for="(card, index) in featureCards" :key="index" class="p-6 rounded-2xl text-center" :style="{ backgroundColor: featureCardBackgroundColor }">
                    <svg class="w-10 h-10 flex-shrink-0 mt-1 mx-auto" :class="featureCardIconColor" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <h3 class="text-lg font-semibold mb-2" :class="featureCardTitleColor">{{ card.title }}</h3>
                    <p class="text-sm" :class="featureCardTextColor" v-html="card.content"></p>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'bg-white'
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    titleHighlightGradientTo: {
        type: String,
        default: '#0C7475'
    },
    titleColor: {
        type: String,
        default: 'text-blue-dark'
    },
    subtitle: {
        type: String,
        default: ''
    },
    subtitleColor: {
        type: String,
        default: 'text-gray-700'
    },
    paragraphs: {
        type: Array,
        default: () => []
    },
    textColor: {
        type: String,
        default: 'text-gray-700'
    },
    image: {
        type: String,
        default: ''
    },
    imageAlt: {
        type: String,
        default: ''
    },
    statsLabel: {
        type: String,
        default: ''
    },
    statsValue: {
        type: String,
        default: ''
    },
    statsSubtext: {
        type: String,
        default: ''
    },
    statsValueColor: {
        type: String,
        default: 'text-blue'
    },
    statsBadgeColor: {
        type: String,
        default: '#FFFFFF'
    },
    featureCards: {
        type: Array,
        default: () => []
    },
    featureCardBackgroundColor: {
        type: String,
        default: '#FFFFFF'
    },
    featureCardTitleColor: {
        type: String,
        default: 'text-blue'
    },
    featureCardTextColor: {
        type: String,
        default: 'text-gray-600'
    },
    featureCardIconColor: {
        type: String,
        default: 'text-blue'
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const highlightRegex = new RegExp(`(${props.titleHighlight})`, 'g');

    return props.title.replace(
        highlightRegex,
        `<span class="bg-gradient-to-t text-transparent bg-clip-text" style="background: linear-gradient(to top, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">$1</span>`
    );
};
</script>
